import React, { useContext, createContext, useMemo, useCallback } from 'react';
import { Step } from 'react-joyride';
import GameTour from './GameTour';
import useTourManager from '../../hooks/useTourManager';
import TourContinuityManager from './TourContinuityManager';
import { useDispatch } from 'react-redux';
import { triggerTourRecalculation, debouncedTourRecalculation } from '../../utils/tour-positioning';
import { useAppTranslation } from '../../i18n/useAppTranslation';

// Define tour configuration type
interface TourConfig {
id: string;
steps: Step[];
autoStart?: boolean;
showSkipButton?: boolean;
showProgress?: boolean;
continuous?: boolean;
spotlightClicks?: boolean;
disableOverlay?: boolean;
disableScrolling?: boolean;
styles?: any;
forcedSteps?: boolean;
showArrows?: boolean;
arrowColor?: string;
arrowSize?: number;
advanceOnTargetClick?: boolean;
advanceDelay?: number;
dependencies?: any[];
}

// Create a context to share tour functions
interface TourContextType {
startTour: (tourId: string) => void;
restartTour: (tourId: string) => void;
completeTour: (tourId: string) => void;
skipTour: (tourId: string) => void;
isTourActive: (tourId: string) => boolean;
isFirstVisit: (tourId: string) => boolean;
getTourProgress: (tourId: string) => number;
recalculatePositions: () => void;
}

const TourContext = createContext<TourContextType | null>(null);

// Hook to use tour context
export const useTour = () => {
const context = useContext(TourContext);
if (!context) {
  throw new Error('useTour must be used within a TourProvider');
}
return context;
};

interface TourProviderProps {
tours: TourConfig[];
children?: React.ReactNode;
renderHelpButton?: boolean;
helpButtonPosition?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
helpButtonStyle?: React.CSSProperties;
helpButtonClassName?: string;
enableContinuity?: boolean;
}

// Help Button Component
interface HelpButtonProps {
className?: string;
style?: React.CSSProperties;
children?: React.ReactNode;
tourId: string;
}

const HelpButton: React.FC<HelpButtonProps> = ({ className = '', style = {}, children, tourId }) => {
const { t } = useAppTranslation("navigation");
const { restartTour } = useTour();

return (
  <button
    onClick={() => restartTour(tourId)}
    className={`tour-help-button ${className}`}
    style={style}
    aria-label={t("navigation:tourGuide")}
    title={t("navigation:tourGuide")}
  >
    {children || (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" />
        <path d="M12 17V17.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M12 14C12 11 15 11.5 15 9C15 7.5 13.5 6 12 6C10.5 6 9.5 7 9 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    )}
  </button>
);
};

// Define the type for the TourProvider component with HelpButton
interface TourProviderComponent extends React.FC<TourProviderProps> {
HelpButton: React.FC<HelpButtonProps>;
}

// Custom hook to manage multiple tours
const useMultipleTourManagers = (tours: TourConfig[]) => {
// console.log("Tour configs:", tours.map(t => ({ id: t.id, autoStart: t.autoStart })));

// Create an array of 5 tour managers (maximum supported)
const manager0 = useTourManager(
  tours[0]?.id || '',
  tours[0]?.autoStart || false,
  tours[0]?.dependencies || []
);
const manager1 = useTourManager(
  tours[1]?.id || '',
  tours[1]?.autoStart || false,
  tours[1]?.dependencies || []
);
const manager2 = useTourManager(
  tours[2]?.id || '',
  tours[2]?.autoStart || false,
  tours[2]?.dependencies || []
);
const manager3 = useTourManager(
  tours[3]?.id || '',
  tours[3]?.autoStart || false,
  tours[3]?.dependencies || []
);
const manager4 = useTourManager(
  tours[4]?.id || '',
  tours[4]?.autoStart || false,
  tours[4]?.dependencies || []
);

// Create a map of active tour managers
const managers: Record<string, ReturnType<typeof useTourManager>> = {};

if (tours[0]?.id) managers[tours[0].id] = manager0;
if (tours[1]?.id) managers[tours[1].id] = manager1;
if (tours[2]?.id) managers[tours[2].id] = manager2;
if (tours[3]?.id) managers[tours[3].id] = manager3;
if (tours[4]?.id) managers[tours[4].id] = manager4;

return managers;
};

/**
 * TourProvider - A component that combines GameTour with useTourManager
 * to provide a simple way to add multiple tours to any component.
 */
const TourProvider: TourProviderComponent = ({
tours,
children,
renderHelpButton = true,
helpButtonPosition = 'top-right',
helpButtonStyle = {},
helpButtonClassName = '',
enableContinuity = true,
}) => {
const { t } = useAppTranslation("navigation");
// Use the custom hook to manage multiple tours
const tourManagers = useMultipleTourManagers(tours);

// Get Redux dispatch function
const dispatch = useDispatch();

// Memoized context value to prevent unnecessary re-renders
const contextValue: TourContextType = useMemo(() => ({
  startTour: (tourId: string) => {
    console.log(`TourProvider: EXPLICIT start tour request for "${tourId}"`);
    
    const manager = tourManagers[tourId];
    if (!manager) {
      console.error(`No tour manager found for ID "${tourId}". Available tours: ${Object.keys(tourManagers).join(', ')}`);
      return;
    }
    
    // CRITICAL: Only start if tour is not already running
    if (manager.showTour) {
      console.warn(`Tour "${tourId}" is already running - ignoring start request`);
      return;
    }
    
    console.log(`Starting tour "${tourId}" through explicit user action`);
    
    // First explicitly dispatch to Redux to ensure both actions are performed
    // Order matters here - first set the active tour
    dispatch({ 
      type: 'SET_ACTIVE_TOUR', 
      payload: tourId 
    });
    
    // Then force the run state to true
    dispatch({ 
      type: 'SET_TOUR_RUN_STATE', 
      payload: { tourId, running: true } 
    });
    
    // Reset the step to 0
    dispatch({
      type: 'UPDATE_TOUR_STEP',
      payload: {
        tourId,
        step: 0
      }
    });
    
    // Finally call the manager's startTour function
    // which will also update local state
    setTimeout(() => {
      console.log(`Executing delayed start for tour "${tourId}"`);
      manager.startTour();
      
      // Trigger position recalculation after tour starts
      setTimeout(() => {
        triggerTourRecalculation();
      }, 200);
    }, 50);
  },
  restartTour: (tourId: string) => {
    const manager = tourManagers[tourId];
    if (manager) {
      manager.restartTour();
    }
  },
  completeTour: (tourId: string) => {
    const manager = tourManagers[tourId];
    if (manager) {
      manager.completeTour();
    }
  },
  skipTour: (tourId: string) => {
    const manager = tourManagers[tourId];
    if (manager) {
      manager.skipTour();
    }
  },
  isTourActive: (tourId: string) => {
    const manager = tourManagers[tourId];
    return manager ? manager.showTour : false;
  },
  isFirstVisit: (tourId: string) => {
    const manager = tourManagers[tourId];
    return manager ? manager.isFirstVisit : false;
  },
  getTourProgress: (tourId: string) => {
    const manager = tourManagers[tourId];
    if (!manager) return 0;
    
    const tour = tours.find(t => t.id === tourId);
    if (!tour) return 0;
    
    const totalSteps = tour.steps.length;
    const currentStep = manager.currentStep;
    
    // Calculate percentage (0-100)
    return Math.round((currentStep / totalSteps) * 100);
  },
  recalculatePositions: () => {
    // Trigger immediate recalculation for all active tours
    triggerTourRecalculation();
  }
}), [tourManagers, dispatch, tours]); // Memoize with stable dependencies

// Memoized position styles for the help button
const positionStyles = useMemo(() => {
  switch (helpButtonPosition) {
    case 'top-right':
      return { top: 20, right: 20 };
    case 'top-left':
      return { top: 20, left: 20 };
    case 'bottom-right':
      return { bottom: 20, right: 20 };
    case 'bottom-left':
      return { bottom: 20, left: 20 };
    default:
      return { top: 20, right: 20 };
  }
}, [helpButtonPosition]);

// Memoized help button click handler
const handleHelpButtonClick = useCallback(() => {
  if (tours.length > 0) {
    contextValue.startTour(tours[0].id);
  }
}, [contextValue, tours]);

// Memoized help button style
const helpButtonFinalStyle = useMemo(() => ({
  position: 'fixed' as const,
  zIndex: 1000,
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: tours[0]?.arrowColor || '#60a020',
  color: '#fff',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
  ...positionStyles,
  ...helpButtonStyle
}), [tours, positionStyles, helpButtonStyle]);

return (
  <TourContext.Provider value={contextValue}>
    {/* Only include TourContinuityManager if continuity is enabled */}
    {enableContinuity && <TourContinuityManager />}
    
    {/* Render GameTour components for each tour */}
    {tours.map((tour) => {
      const manager = tourManagers[tour.id];
      const shouldForceTour = (tour.forcedSteps ?? false) && manager.isFirstVisit;
      
      // console.log(`Rendering GameTour for "${tour.id}":`, {
      //   isActive: manager.showTour,
      //   isFirstVisit: manager.isFirstVisit,
      //   steps: tour.steps.length,
      //   shouldForce: shouldForceTour
      // });
      
      return (
        <GameTour
          key={tour.id}
          steps={tour.steps}
          tourId={tour.id}
          showSkipButton={tour.showSkipButton ?? true}
          showProgress={tour.showProgress ?? true}
          continuous={tour.continuous ?? true}
          isActive={manager.showTour}
          onComplete={manager.completeTour}
          onSkip={manager.skipTour}
          spotlightClicks={tour.spotlightClicks ?? false}
          disableOverlay={tour.disableOverlay ?? false}
          disableScrolling={tour.disableScrolling ?? false}
          styles={tour.styles ?? {}}
          forcedSteps={shouldForceTour}
            showArrows={tour.showArrows ?? false}
            arrowColor={tour.arrowColor ?? '#60a020'}
            arrowSize={tour.arrowSize ?? 40}
            advanceOnTargetClick={tour.advanceOnTargetClick ?? false}
            advanceDelay={tour.advanceDelay ?? 0}
          />
        );
      })}

      {/* Help Button (optional) */}
      {renderHelpButton && tours.length > 0 && (
        <button
          onClick={handleHelpButtonClick}
          className={`tour-help-button ${helpButtonClassName}`}
          style={helpButtonFinalStyle}
          aria-label={t("navigation:tourGuide")}
          title={t("navigation:tourGuide")}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="white" strokeWidth="2" />
            <path d="M12 17V17.01" stroke="white" strokeWidth="2" strokeLinecap="round" />
            <path d="M12 14C12 11 15 11.5 15 9C15 7.5 13.5 6 12 6C10.5 6 9.5 7 9 8" stroke="white" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      )}

      {/* Render children */}
      {children}
    </TourContext.Provider>
  );
};


// Attach HelpButton to TourProvider
TourProvider.HelpButton = HelpButton;

export default TourProvider; 
